import { Component, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'
import { ApiWalletService } from '@modules/private/wallet/data/services/api/api-wallet.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

type OffBehavior = 'PAUSE' | 'KEEP_CHARGING'

//* Una entrada del historial ya traducida: el template solo la pinta.
export interface RuleChange {
	date: string
	/** Nombre humano del campo. El documento guarda la ruta (`mora.offBehavior`). */
	label: string
	before: string
	after: string
	reason: string
	/** Familia de la regla; decide el color del punto. */
	tone: 'on' | 'off' | 'mora' | 'dup' | 'quality'
	/** La matriz completa no cabe en una línea: se pinta en dos renglones. */
	stacked: boolean
	user: string
}

@Component({
	selector: 'app-wallet-rules',
	templateUrl: './wallet-rules.component.html',
	styleUrls: ['./wallet-rules.component.scss'],
	providers: [MessageService],
})
export class WalletRulesComponent implements OnInit {
	settings: any = null
	draft: any = null
	/** Tasa default capturada en % (PRD F30 «tasa default (%)»), la misma convención que Contrato (§6.12); viaja en decimal. */
	ratePct: number | null = null
	/**
	 * Puntualidad mínima de cada regla, capturada en % igual que la tasa.
	 *
	 * El documento la guarda en decimal (0.90) y así viaja al backend, pero pedirla así en pantalla
	 * obligaba a escribir «0,90» para un dato que el resto del panel enseña como «90 %» —la banda de
	 * comportamiento de Exigibilidad, sin ir más lejos—. Índice a índice contra `grades`.
	 */
	gradePct: (number | null)[] = []
	reason = ''
	loading = false
	saving = false
	error = ''
	readonly offBehaviors: { label: string, value: OffBehavior, help: string }[] = [
		{ label: 'En pausa', value: 'PAUSE', help: 'Lo ya devengado queda guardado y visible, pero fuera del pendiente, de la elegibilidad, del reparto y de la cartera. Vuelve a ser exigible al encender.' },
		{ label: 'Seguir cobrando', value: 'KEEP_CHARGING', help: 'Solo se detiene el devengo nuevo. Lo ya devengado se sigue cobrando por prelación y cuenta en cartera.' },
	]

	//* Nombre humano de cada campo que el backend registra en `history` (wallet-settings.service.ts).
	private readonly fieldLabels: Record<string, string> = {
		'mora.enabled': 'Interruptor de mora',
		'mora.offBehavior': 'Comportamiento al apagar',
		'mora.defaultMonthlyRate': 'Tasa mensual por defecto',
		'mora.graceDays': 'Días de gracia',
		'duplicates.windowDays': 'Ventana de duplicados',
		'paymentQuality.windowPeriods': 'Ventana de calificación',
		'paymentQuality.grades': 'Matriz de calificación',
	}

	constructor(private apiWalletService: ApiWalletService, private authService: AuthService, private messageService: MessageService) {}

	/** Solo Admin edita (D15). El backend lo revalida: `PUT /wallet/settings` exige `wallet:settings`. */
	get canEdit(): boolean {
		return this.authService.hasModulePermission('wallet', 'settings')
	}

	ngOnInit(): void {
		this.load()
	}

	load(): void {
		this.loading = true
		this.apiWalletService.getSettings(true).subscribe((r) => {
			this.loading = false
			if (r.error) {
				this.error = r.msg
				return
			}
			this.settings = r.data
			this.draft = structuredClone(r.data)
			this.ratePct = this.toPct(r.data?.mora?.defaultMonthlyRate)
			this.gradePct = (this.draft?.paymentQuality?.grades ?? []).map((g: any) => this.toPct(g.minOnTimeRate))
		})
	}

	/** El % capturado se vuelca al borrador en decimal (0.035 = 3.5 %); así `isDirty` y `save()` no cambian. */
	onRatePctChange(): void {
		this.draft.mora.defaultMonthlyRate = this.toDecimal(this.ratePct)
	}

	/** Lo mismo para la puntualidad mínima de una regla de la matriz. */
	onGradePctChange(index: number): void {
		const grade = this.draft?.paymentQuality?.grades?.[index]
		if (grade) grade.minOnTimeRate = this.toDecimal(this.gradePct[index])
	}

	get isDirty(): boolean {
		return !!this.draft && JSON.stringify({ m: this.draft.mora, q: this.draft.paymentQuality, d: this.draft.duplicates }) !== JSON.stringify({ m: this.settings.mora, q: this.settings.paymentQuality, d: this.settings.duplicates })
	}

	get lastGradeHasConditions(): boolean {
		const g = this.draft?.paymentQuality?.grades ?? []
		const last = g[g.length - 1]
		return !!last && (last.minOnTimeRate != null || last.maxDelayDays != null || last.maxOpenOverdue != null)
	}

	/**
	 * Qué reglas cambiaron respecto de lo guardado, con su nombre humano.
	 *
	 * La lista replica campo por campo la del backend (`WalletSettingsService.update`), que es la que
	 * decide qué entra al historial: si allá se agrega una regla nueva, aquí también.
	 */
	get pendingChanges(): string[] {
		if (!this.draft || !this.settings) return []
		const cambiados: string[] = []
		const comparar = (campo: string, antes: any, ahora: any) => {
			if (JSON.stringify(antes) !== JSON.stringify(ahora)) cambiados.push(this.fieldLabels[campo])
		}
		comparar('mora.enabled', this.settings.mora.enabled, this.draft.mora.enabled)
		comparar('mora.offBehavior', this.settings.mora.offBehavior, this.draft.mora.offBehavior)
		comparar('mora.defaultMonthlyRate', this.settings.mora.defaultMonthlyRate, this.draft.mora.defaultMonthlyRate)
		comparar('mora.graceDays', this.settings.mora.graceDays, this.draft.mora.graceDays)
		comparar('duplicates.windowDays', this.settings.duplicates.windowDays, this.draft.duplicates.windowDays)
		comparar('paymentQuality.windowPeriods', this.settings.paymentQuality.windowPeriods, this.draft.paymentQuality.windowPeriods)
		comparar('paymentQuality.grades', this.settings.paymentQuality.grades, this.draft.paymentQuality.grades)
		return cambiados
	}

	/** Si una tarjeta tiene algo sin guardar, para que lo diga en su propia cabecera. */
	sectionDirty(seccion: 'mora' | 'windows' | 'grades'): boolean {
		if (!this.draft || !this.settings) return false
		const igual = (antes: any, ahora: any) => JSON.stringify(antes) === JSON.stringify(ahora)
		if (seccion === 'mora') return !igual(this.settings.mora, this.draft.mora)
		if (seccion === 'grades') return !igual(this.settings.paymentQuality.grades, this.draft.paymentQuality.grades)
		//* La tarjeta de la derecha reúne dos ventanas de secciones distintas del documento.
		return !igual(this.settings.duplicates, this.draft.duplicates) || !igual(this.settings.paymentQuality.windowPeriods, this.draft.paymentQuality.windowPeriods)
	}

	/** Ayuda del comportamiento elegido. Antes el template recorría las dos opciones y emitía un `<small>` vacío por la descartada. */
	get offBehaviorHelp(): string {
		return this.offBehaviors.find((o) => o.value === this.draft?.mora?.offBehavior)?.help ?? ''
	}

	/**
	 * Por qué el botón de guardar está apagado. Un botón deshabilitado sin explicación es un callejón
	 * sin salida: aquí hay tres motivos distintos y ninguno se veía en pantalla.
	 */
	get saveHint(): string {
		if (this.lastGradeHasConditions) return 'La última calificación no debe llevar condiciones: es la que recibe quien no cumple ninguna otra.'
		if (!this.isDirty) return 'No hay cambios que guardar.'
		if (!this.reason.trim()) return 'Escribe el motivo para poder guardar.'
		return 'Cada campo que cambies queda en el historial con este motivo.'
	}

	get canSave(): boolean {
		return this.isDirty && !!this.reason.trim() && !this.lastGradeHasConditions && !this.saving
	}

	/** Color del cuadro de cada calificación: el mismo código que la banda de comportamiento de Exigibilidad. */
	gradeClass(grade: string): string {
		return 'is-' + (grade || 'none').toUpperCase()
	}

	/**
	 * El historial, del más reciente al más antiguo y en palabras.
	 *
	 * El documento guarda la ruta del campo y los valores crudos: la pestaña enseñaba
	 * `mora.offBehavior` y `"PAUSE" → "KEEP_CHARGING"`, comillas incluidas.
	 */
	private historyCacheSource: any[] | null = null
	private historyCache: RuleChange[] = []
	get history(): RuleChange[] {
		//* Memoizado contra la referencia de origen: el template lo recorre en cada ciclo de detección
		//* de cambios y un arreglo nuevo cada vez repintaría el historial entero sin parar.
		const origen = this.settings?.history ?? null
		if (origen === this.historyCacheSource) return this.historyCache
		this.historyCacheSource = origen
		const entradas = [...(origen ?? [])]
		entradas.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
		this.historyCache = entradas.map((h: any) => ({
			date: h.date,
			label: this.fieldLabels[h.field] || h.field || 'Regla',
			before: this.formatValue(h.field, h.previousValue),
			after: this.formatValue(h.field, h.newValue),
			reason: h.reason || '',
			tone: this.toneOf(h),
			stacked: h.field === 'paymentQuality.grades',
			//* El backend no puebla `user`: se enseña el nombre solo cuando llega de verdad, en vez de
			//* firmar cada cambio con un «Usuario» que no dice nada.
			user: [h.user?.names?.firstName, h.user?.names?.lastName].filter(Boolean).join(' '),
		}))
		return this.historyCache
	}

	save(): void {
		if (!this.canEdit || !this.reason.trim()) return
		this.saving = true
		const payload = { mora: this.draft.mora, paymentQuality: this.draft.paymentQuality, duplicates: this.draft.duplicates, reason: this.reason.trim() }
		this.apiWalletService.putSettings(payload).subscribe((r) => {
			this.saving = false
			if (r.error) {
				this.messageService.add({ severity: 'error', summary: 'No se guardaron las reglas', detail: r.msg, life: 8000 })
				return
			}
			this.reason = ''
			this.messageService.add({ severity: 'success', summary: 'Reglas guardadas', detail: 'Cada campo cambiado quedó en el historial.', life: 6000 })
			this.load()
		})
	}

	private toPct(decimal: number | null | undefined): number | null {
		//* Redondeo a dos decimales: 0.9 * 100 da 90.00000000000001 en punto flotante.
		return decimal == null ? null : Math.round(Number(decimal) * 10000) / 100
	}

	private toDecimal(pct: number | null): number {
		return Number(((pct ?? 0) / 100).toFixed(4))
	}

	private toneOf(h: any): RuleChange['tone'] {
		if (h.field === 'mora.enabled') return h.newValue ? 'on' : 'off'
		if (String(h.field).startsWith('mora.')) return 'mora'
		if (String(h.field).startsWith('duplicates.')) return 'dup'
		return 'quality'
	}

	/** Cada campo se lee en sus propias unidades; la matriz se resume regla por regla. */
	private formatValue(field: string, value: any): string {
		if (value == null) return '—'
		switch (field) {
			case 'mora.enabled': return value ? 'Encendida' : 'Apagada'
			case 'mora.offBehavior': return this.offBehaviors.find((o) => o.value === value)?.label ?? String(value)
			case 'mora.defaultMonthlyRate': return `${this.toPct(value)} %`
			case 'mora.graceDays': return `${value} ${value === 1 ? 'día' : 'días'}`
			case 'duplicates.windowDays': return `${value} ${value === 1 ? 'día' : 'días'}`
			case 'paymentQuality.windowPeriods': return `${value} ${value === 1 ? 'letra' : 'letras'}`
			case 'paymentQuality.grades': return (value as any[]).map((g) => (g.minOnTimeRate != null ? `${g.grade} ≥ ${this.toPct(g.minOnTimeRate)} % · ${g.maxDelayDays ?? '—'} d · ${g.maxOpenOverdue ?? '—'}` : `${g.grade} el resto`)).join('  ·  ')
			default: return typeof value === 'object' ? JSON.stringify(value) : String(value)
		}
	}
}
